import {
  pgTable,
  uuid,
  text,
  timestamp,
  numeric,
  integer,
  index,
  pgPolicy,
  check,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants } from './users.js';

/**
 * Phase 8 / Task 8.5 (§20) — geofence event audit trail. Every geofence trigger
 * (auto or manual) writes a row carrying the GPS, distance, dwell, and §15 GPS
 * verification result. site_id is polymorphic (company_sites / disposal_facilities
 * / yards) per §86 — no FK. RLS-scoped to tenant.
 */
export const geofenceEvents = pgTable(
  'geofence_events',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    jobId: uuid('job_id'),
    driverId: uuid('driver_id'),
    siteId: uuid('site_id').notNull(),
    eventType: text('event_type').notNull(),
    triggeredBy: text('triggered_by').notNull(),
    geofenceType: text('geofence_type'),
    gpsLat: numeric('gps_lat', { precision: 10, scale: 7 }),
    gpsLng: numeric('gps_lng', { precision: 10, scale: 7 }),
    gpsAccuracyFt: integer('gps_accuracy_ft'),
    distanceFromCenter: numeric('distance_from_center', { precision: 10, scale: 2 }),
    dwellSeconds: integer('dwell_seconds'),
    verificationResult: text('verification_result'),
    verificationThresholdFt: integer('verification_threshold_ft'),
    occurredAt: timestamp('occurred_at', { withTimezone: true }).notNull(),
    syncedAt: timestamp('synced_at', { withTimezone: true }).notNull().defaultNow(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('geofence_events_tenant_job_idx').on(t.tenantId, t.jobId),
    index('geofence_events_tenant_driver_idx').on(t.tenantId, t.driverId),
    check(
      'geofence_events_event_type_check',
      sql`event_type IN ('GEOFENCE_ENTERED','GEOFENCE_EXITED')`,
    ),
    check('geofence_events_triggered_by_check', sql`triggered_by IN ('GEOFENCE_AUTO','MANUAL')`),
    check(
      'geofence_events_geofence_type_check',
      sql`geofence_type IS NULL OR geofence_type IN ('RADIUS','POLYGON')`,
    ),
    check(
      'geofence_events_verification_result_check',
      sql`verification_result IS NULL OR verification_result IN ('VERIFIED','OUTSIDE_RANGE','UNVERIFIABLE','NO_GPS')`,
    ),
    pgPolicy('geofence_events_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type GeofenceEvent = typeof geofenceEvents.$inferSelect;
export type NewGeofenceEvent = typeof geofenceEvents.$inferInsert;
export const geofenceEventSelectSchema = createSelectSchema(geofenceEvents);
export const geofenceEventInsertSchema = createInsertSchema(geofenceEvents);
